// APIS (Autonomous Power Interchange System) Web API client
// Endpoint docs: https://github.com/hyphae/apis-web/blob/master/doc/en/apis-web_specification_en.md

const BASE_URL = process.env.REACT_APP_APIS_BASE_URL ?? 'http://localhost:4382';
const FETCH_TIMEOUT_MS = 5000;

// ---------------------------------------------------------------------------
// Raw APIS types (matching actual JSON from hyphae/apis-emulator)
// ---------------------------------------------------------------------------

export interface APISEmuData {
  rsoc: number;               // Relative State of Charge (%)
  pvc_charge_power: number;   // Solar PV charging power (W)
  pvc_charge_current: number; // Solar PV charging current (A)
  pvc_charge_voltage: number; // Solar PV charging voltage (V)
  ups_output_power: number;   // Load/home output power (W)
  ups_output_current: number; // Load output current (A)
  ups_output_voltage: number; // Load output voltage (V)
  ups_input_voltage: number;  // UPS input voltage (V)
  battery_voltage: number;    // Battery voltage (V)
  battery_current: number;    // Battery current (A, negative = discharging)
  battery_rsoc: number;       // Battery RSoC (%)
  charge_discharge_power: number; // Battery charge/discharge power (W)
  dischargeable_time: { hour: number; minute: number };
}

export interface APISMeter {
  wg: number;   // Grid power (W, positive = import, negative = export)
  wb: number;   // Battery power (W)
  vg: number;   // Grid voltage (V)
  vb: number;   // Battery voltage (V)
  ig: number;   // Grid current (A)
  ib: number;   // Battery current (A)
  tmp: number;  // Temperature (°C)
}

export interface APISPowermeter {
  p1: number;   // Grid side power (W)
  p2: number;   // Battery side power (W)
  v1: number;   // Grid side voltage (V)
  i1: number;   // Grid side current (A)
  kwh1: number; // Grid side energy (kWh)
  kwh2: number; // Battery side energy (kWh)
}

export interface APISNode {
  emu: APISEmuData;
  dcdc: {
    meter: APISMeter;
    powermeter: APISPowermeter;
    status: {
      statusName: string;
      operationMode: string;
      runningState: string;
    };
  };
  oesunit: {
    id: string;
    display: string;
    on: boolean;
  };
  time: string;
}

// /get/log response: object keyed by unit ID (e.g. "E001", "E002")
export type APISLogResponse = Record<string, APISNode>;

// /deals response: array of active P2P energy interchange deals
export interface APISDeal {
  dealId: string;
  negotiationId: string;
  requestUnitId: string;     // Node that requested the interchange
  acceptUnitId: string;      // Node that accepted the interchange
  chargeUnitId: string;      // Charging (buying) node
  dischargeUnitId: string;   // Discharging (selling) node
  type: 'charge' | 'discharge';
  dealAmountWh: number;      // Energy amount (Wh)
  dealGridCurrentA: number;  // Current during interchange (A)
  requestAmountWh: number;
  acceptAmountWh: number;
  pointPerWh: number;        // Price points per Wh
  requestPointPerWh: number;
  acceptPontPerWh: number;   // Note: typo is in the APIS spec
  requestDateTime: string;
  acceptDateTime: string;
  createDateTime: string;
  activateDateTime: string;
}

export type APISDealsResponse = APISDeal[];

// /unitIds response: array of node IDs
export type APISUnitIdsResponse = string[];

// /getStatus response
export interface APISStatusResponse {
  global: {
    mode: string; // "autonomous" | "manual" | "stop" | "run" | "quiet"
  };
}

// ---------------------------------------------------------------------------
// Fetch helper
// ---------------------------------------------------------------------------

async function fetchWithTimeout(url: string): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { signal: controller.signal });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function apiFetch<T>(path: string): Promise<T> {
  const response = await fetchWithTimeout(`${BASE_URL}${path}`);
  if (!response.ok) {
    throw new Error(`APIS ${path} returned ${response.status}`);
  }
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Exported API functions
// ---------------------------------------------------------------------------

export async function getLog(): Promise<APISLogResponse> {
  return apiFetch<APISLogResponse>('/get/log');
}

export async function getDeals(): Promise<APISDealsResponse> {
  return apiFetch<APISDealsResponse>('/deals');
}

export async function getUnitIds(): Promise<APISUnitIdsResponse> {
  return apiFetch<APISUnitIdsResponse>('/unitIds');
}

export async function getStatus(): Promise<APISStatusResponse> {
  return apiFetch<APISStatusResponse>('/getStatus');
}
